import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, model, OnInit } from '@angular/core';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { TranslatePipe } from '@ngx-translate/core';
import { simpleDeepClone } from '@phx-erp/shared';
import { ButtonModule } from 'primeng/button';
import { Table } from 'primeng/table';
import { lastValueFrom } from 'rxjs';
import { BaseListCombinedService } from '../../../../@core/services/base-list-combined.service';
import { ColumnConfigEx, ColumnDefinitionEx } from '../../../../@core/types/column-config-types-extended';
import { Address, AddressDocumentExport, AddressSearchInput, DocumentTransferFormat, Operator } from '../../../../generated/generated-types';
import { BaseListWrapperComponent } from '../../../../shared/components/base-list-wrapper/base-list-wrapper.component';
import { NewBaseListComponent } from '../../../../shared/components/new-base-list-component/new-base-list-component';
import { PhxInputComponent } from '../../../../shared/components/phx-input/phx-input.component';
import { DocumentTransferFormatDropDownComponent } from '../../../admin/modules/document-transfer-format-drop-down/document-transfer-format-drop-down.component';
import { DocumentDefinitionService } from '../../../documents/services/document-definition.service';
import { AddressDocumentExportService } from '../../services/address-document-export.service';
import { AddressDocumentExportEditWidgetComponent } from '../address-document-export-edit-widget/address-document-export-edit-widget.component';

@Component({
  selector: 'app-address-document-export-list-widget',
  templateUrl: 'address-document-export-list-widget.component.html',
  providers: [AddressDocumentExportService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    BaseListWrapperComponent,
    ButtonModule,
    TranslatePipe,
    CommonModule,
  ]
})
export class AddressDocumentExportListWidgetComponent extends NewBaseListComponent<AddressDocumentExport> implements OnInit {

  address = model.required<Address>();
  notFinanceRelevant = model<boolean>(true);

  //button only when there is no e-invoice connection yet
  showAddEInvoiceDetails = model<boolean>(false);
  showAddEInvoiceDetailsButtonLoading = model<boolean>(false);

  constructor(
    private addressDocumentExportService: AddressDocumentExportService,
    private docDefService: DocumentDefinitionService,
    private baseListServices: BaseListCombinedService) {
    super("AddressDocumentExport", addressDocumentExportService);
  }

  ngOnInit(): void {
    this.inlineEditMode = false;
    this.addInDetail = false;
    this.dialogEditMode = true;
    this.addInDialog = true;
    this.noCopy = true;
    super.ngOnInit();
  }

  override getSearchInputForQuery() {
    let si: AddressSearchInput = super.getSearchInputForQuery();
    if (this.address().id)
      si.searchFilter = {
        field: "addressId",
        operator: Operator.equal,
        value: this.address().id
      }
    return si;
  }

  override showEditDialog(rowData?: AddressDocumentExport, index?: number) {
    const ref = this.baseListServices.modalService.fromComponent(AddressDocumentExportEditWidgetComponent, {
      size: 'md',
      verticalAlign: 'top',
      closable: true,
      title: this.baseListServices.i18nService.translate(_('address-document-export.add-edit')),
      locals: {
        detailModel: rowData ?? null
      },
      localsSig: {
        address: this.address,
        existingItemsSig: this.itemsSig
      }
    })

    let sub = ref.subscribe((data: AddressDocumentExport) => {
      // refetch instead of splicing the row in locally: the local splice left the paginator
      // total untouched ("0 - 0 von 0" with a visible row) and hid persistence failures (DEV-3053)
      if (data)
        this.refresh(true);
      sub.unsubscribe();
    });
  }

  onRowEditSave(rowData: AddressDocumentExport): Promise<AddressDocumentExport> {
    rowData.addressId = this.address().id;
    const docDefId = rowData.documentDefinitionId ?? rowData.documentDefinition?.id;
    if (this.itemsSig().some(x => x.documentDefinitionId === docDefId)) {
      this.baseServices.notificationService.error(_('address-document-export.document-definition-already-exists'));
      this.onRowEditCancel(rowData);
      return;
    }
    this.checkIfEInvoiceButtonShouldShow();
    return super.onRowEditSave(rowData);
  }

  protected async setColumnConfig(colConfig: ColumnConfigEx, table?: Table) {

    for (let cd of colConfig.columns) {

      //TODO do we even need this if we have detail edit?

      // it has to be product identifier to get the dropdown for choosing the product
      // if (cd.field == "documentDefinition.identifier") {
      //   cd.getEditComponent = () => DocumentDefinitionDropdownComponent;
      //   cd.editComponentCreated = async (c: DocumentDefinitionDropdownComponent, rowData: AddressDocumentExport, colDef: ColumnDefinitionEx) => {

      //     //propertyBinding style - set in dropdown itself from this parent
      //     if (rowData.documentDefinition)
      //       c.setModel(rowData.documentDefinition);
      //     else if (rowData.documentDefinitionId) {
      //       await c.loadItemByIdAndSetSelected(rowData.documentDefinitionId);
      //       this.cdRef.detectChanges();
      //     }

      //     //eventBinding style - set in this parent from component
      //     c.formControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(async (x: DocumentDefinition) => {
      //       rowData.documentDefinition = x;
      //       rowData.documentDefinitionId = x?.id;
      //       if (x?.financeRelevant)
      //         this.notFinanceRelevant.set(false);
      //       else
      //         this.notFinanceRelevant.set(true);
      //     });
      //   }
      // }
      // else 
      if (cd.field == "documentDefinition.description") {
        cd.getEditComponent = () => PhxInputComponent;
        cd.editComponentCreated = (c: PhxInputComponent | any, rowData: AddressDocumentExport, colDef: ColumnDefinitionEx) => {
          c.disabled.set(true);
        }
      }
      else if (cd.field == "documentTransferFormat.identifier") {
        cd.getEditComponent = () => DocumentTransferFormatDropDownComponent;
        cd.editComponentCreated = async (c: DocumentTransferFormatDropDownComponent, rowData: AddressDocumentExport, colDef: ColumnDefinitionEx) => {
          c.withoutFinancialFormats = this.notFinanceRelevant;
          //propertyBinding style - set in dropdown itself from this parent
          if (rowData.documentTransferFormat)
            c.setModel(rowData.documentTransferFormat);
          else if (rowData.documentTransferFormatId) {
            await c.loadItemByIdAndSetSelected(rowData.documentTransferFormatId);
            this.cdRef.detectChanges();
          }

          //eventBinding style - set in this parent from component
          c.value.subscribe(async (x: DocumentTransferFormat) => {
            rowData.documentTransferFormatId = x?.id;
          });
        }
      }
      else if (cd.field == "documentTransferFormat.description") {
        cd.getEditComponent = () => PhxInputComponent;
        cd.editComponentCreated = (c: PhxInputComponent | any, rowData: AddressDocumentExport, colDef: ColumnDefinitionEx) => {
          c.disabled.set(true);
        }
      }

      //TODO Contact / emailAddresses - if this wants to be used inlineEditMode 
    }
    super.setColumnConfig(colConfig);
  }

  override setItems(items: AddressDocumentExport[], totalCount?: number) {
    super.setItems(items, totalCount);
    this.checkIfEInvoiceButtonShouldShow();
  }

  checkIfEInvoiceButtonShouldShow() {

    let anyEInvoice = false;
    if (this.address()?.isDebitor) {
      let totalCount = this.table.totalRecords;
      let items = this.itemsSig();

      //lets just not show the button if we have a huge list with paging
      if (totalCount <= items.length) {
        for (let i = 0; i < items.length; i++) {
          const element = items[i];

          if (element.documentDefinition?.financeRelevant) {
            const identifier = element.documentTransferFormat?.identifier?.toLowerCase();;
            if (identifier.includes("x-rechnung") || identifier.includes("zugferd")) {
              anyEInvoice = true;
              break;
            }
          }
        }
      }
    }

    this.showAddEInvoiceDetails.set(!anyEInvoice);
  }

  async addEInvoiceDetails() {

    this.showAddEInvoiceDetailsButtonLoading.set(true);

    let def: AddressDocumentExport = {} as AddressDocumentExport;

    let docDefs = await lastValueFrom(await this.docDefService.getItems({
      searchFilter: {
        rules: [
          {
            field: "financeRelevant",
            operator: Operator.equal,
            value: "true"
          },
          {
            field: "documentDirection",
            operator: Operator.equal,
            value: "OUT"
          }
        ]
      }
    }).mapSingle(x => x.getDocumentDefinitions));

    if (docDefs.totalItems == 1) {
      def.documentDefinition = simpleDeepClone(docDefs.items[0]);
      def.documentDefinitionId = docDefs.items[0].id;
    }

    this.addNewItem(null, def);

    this.showAddEInvoiceDetailsButtonLoading.set(false);
  }

}
